import "./LoginView.less";

import { notification, Button, Form, Icon, Input, Spin } from "antd";
import { curry, __ } from "lodash/fp";
import React, { useEffect, useState } from "react";

import { CancellationToken } from "lib/api/base";
import { PasswordApi } from "lib/api/security/passwordReset";
import { IBaseProps } from "lib/data/interfaces";
import { ExpiredLoginResult, LockedLoginResult, LoginAttempt, PasswordResetRequest, SuccessLoginResult } from "lib/data/model";
import { PasswordResetRequestStatus } from "lib/data/model/enumerations";
import { TimeSpan } from "lib/data/transfer";

import { TransitionRoute } from "components/general/TransitionRoute";
import { LoginService, StateService } from "lib/services";
import { Event, EventDispatcher } from "lib/EventDispatcher";


interface ILoginViewProps extends IBaseProps {
	onStageChange?: (name: string) => void;
}

type LoginStage = "login" | "reset" | "change";

const defaultStage: LoginStage = "login";
const stageDescriptions = {
	login: "Login",
	reset: "Request Password Reset",
	change: "Change Password"
};

const LoginViewFC = (props: ILoginViewProps) => {

	const [cancellationToken] = useState(new CancellationToken());
	const [className, setClassName] = useState<string>("login-view");
	const [isLoading, setLoading] = useState<boolean>(true);
	const [isLoggedIn, setLoggedIn] = useState<boolean>(false);
	const [stage, setStage] = useState<LoginStage>(defaultStage);
	const [message, setMessage] = useState<string | undefined>();
	const [loginUsername, setLoginUsername] = useState<string>("");
	const [loginPassword, setLoginPassword] = useState<string>("");
	const [resetUsername, setResetUsername] = useState<string>("");
	const [changePassword, setChangePassword] = useState<string>("");
	const [changePasswordConfirmation, setChangePasswordConfirmation] = useState<string>();
	const [doChangePasswordsMatch, setChangePasswordsMatch] = useState<boolean>();
	const [passwordResetRequest, setPasswordResetRequest] = useState<PasswordResetRequest>();
	const [hasSentLoginEvent, setHasSentLoginEvent] = useState<boolean>();

	useEffect(() => {

		const isAuthenticated = async () => {

			const isCurrentlyLoggedIn = await LoginService.isLoggedIn();

			StateService.setCancelableState(() => {
				setLoggedIn(isCurrentlyLoggedIn);
				setStage(defaultStage);
				setLoading(false);
			}, cancellationToken);
		};

		isAuthenticated();

		return () => {

			cancellationToken.cancel();
		};
	}, []);

	useEffect(() => {

		if (props.onStageChange !== undefined) {

			// Provide Initial Stage Name
			props.onStageChange(stageDescriptions[stage]);
		}
	}, [stage]);

	useEffect(() => {

		const classNames = [
			"login-view",
			props.className
		];

		setClassName(classNames.filter((c) => c !== undefined).join(" ").trim());
	}, [props.className]);


	const onSetStage = (_event: React.MouseEvent, newStage: LoginStage) => {

		StateService.setCancelableState(() => {
			setStage(newStage);
			setMessage(undefined);
			setLoginUsername("");
			setLoginPassword("");
			setResetUsername("");
			setChangePassword("");
			setChangePasswordConfirmation("");
			setChangePasswordsMatch(false);
		}, cancellationToken);
	};

	const onLoginUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		StateService.setCancelableState(() => setLoginUsername(event.target.value), cancellationToken);
	};

	const onLoginPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		StateService.setCancelableState(() => setLoginPassword(event.target.value.trim()), cancellationToken);
	};

	const onLogin = async () => {

		const loginResult = await LoginService.login(loginUsername, loginPassword);

		// Check Login Result
		if (loginResult !== undefined) {

			// Initialize Message
			let loginMessage: string | undefined;

			// Switch On Login Result Type
			switch (loginResult.$type) {

				case SuccessLoginResult.$type:

					// Notify User
					notification.success({
						message: "Login Successful"
					});

					// Clear Message
					StateService.setCancelableState(() => setMessage(undefined), cancellationToken);

					break;

				case ExpiredLoginResult.$type:

					// Cast Result
					const expiredResult = loginResult as ExpiredLoginResult;

					// Set Message
					loginMessage = "Your Password Needs To Be Changed";

					// Notify User
					notification.warning({
						message: loginMessage
					});

					// Store Password Reset Request
					setPasswordResetRequest(expiredResult.resetRequest);

					// Change Stage
					setStage("change");

					// Set Message
					StateService.setCancelableState(() => setMessage(loginMessage), cancellationToken);

					break;

				case LockedLoginResult.$type:

					// Cast Result
					const lockedResult = loginResult as LockedLoginResult;

					// Set Message
					loginMessage = "Account Locked";

					// Notify User
					notification.warning({
						message: loginMessage,
						description: `Time remaining: ${TimeSpan.toString(lockedResult.lockoutTimeRemaining)} (hh:mm:ss)`
					});

					// Change Stage & Set Message
					StateService.setCancelableState(() => {
						setMessage(`${loginMessage}. Please Wait: ${TimeSpan.toString(lockedResult.lockoutTimeRemaining)} (hh:mm:ss)`);
						setLoginPassword("");
					}, cancellationToken);

					break;

				case LoginAttempt.$type:
				default:

					// Set Message
					loginMessage = "Invalid Username and/or Password";

					// Notify User
					notification.warning({
						message: loginMessage
					});

					// Change Stage & Set Message
					StateService.setCancelableState(() => {
						setMessage(loginMessage);
						setLoginPassword("");
					}, cancellationToken);

					break;
			}
		}
		else {

			// Inform User
			notification.error({
				message: "Error Logging In",
				description: "An unexpected error has occurred.  Please contact your system administrator."
			});

			// Set Error Message
			StateService.setCancelableState(() => setMessage("Error Logging In"), cancellationToken);
		}
	};

	const onResetUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		StateService.setCancelableState(() => setResetUsername(event.target.value), cancellationToken);
	};

	const onReset = async () => {

		// Request Reset
		const isRequested = await PasswordApi.requestPasswordReset(resetUsername);

		// Check Reset Request
		if (isRequested) {

			// Inform User
			notification.success({
				message: "Success",
				description: "Requested password reset. Check the email associated with the username for additonal information."
			});

			// Change Stage
			StateService.setCancelableState(() => {
				setStage(defaultStage);
				setMessage(undefined);
			}, cancellationToken);
		}
		else {

			// Inform User
			notification.error({
				message: "Error Requesting Password Reset",
				description: "An unexpected error has occurred.  Please contact your system administrator."
			});

			// Set Error Message
			StateService.setCancelableState(() => setMessage("Error Requesting Password Reset"), cancellationToken);
		}
	};

	const onChangePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		// Get Value
		const value = event.target.value;

		// Check Value
		if (value.includes(" ")) {

			// Notify User
			StateService.setCancelableState(() => setMessage("Passwords Cannot Contain Spaces"), cancellationToken);
		}
		else {

			// Set Value
			StateService.setCancelableState(() => setChangePassword(value), cancellationToken);
		}
	};

	const onChangePasswordConfirmChange = (event: React.ChangeEvent<HTMLInputElement>) => {

		// Get Value
		const value = event.target.value;

		// Check Value
		if (value !== changePassword) {

			// Set Value & Inform User
			StateService.setCancelableState(() => {
				setChangePasswordConfirmation(value);
				setMessage("Passwords Do Not Match");
				setChangePasswordsMatch(false);
			}, cancellationToken);
		}
		else {

			// Set Value & Inform User
			StateService.setCancelableState(() => {
				setChangePasswordConfirmation(value);
				setMessage(undefined);
				setChangePasswordsMatch(true);
			}, cancellationToken);
		}
	};

	const onChange = async () => {

		// Ensure Passwords Match
		if (doChangePasswordsMatch) {

			// Initialize Success Flag
			let isSuccess: boolean = false;
			let isExpired: boolean = false;

			// Check Expired Password Reset Request
			if (passwordResetRequest !== undefined) {

				// Get Latest Reset Request
				const resetRequest = await PasswordApi.getPasswordResetToken(passwordResetRequest.token);

				// Check Latest Reset Request
				if (resetRequest !== undefined) {

					// Check Reset Request Status
					if (resetRequest.status === PasswordResetRequestStatus.Requested) {

						// Request Reset
						const isReset = await PasswordApi.resetPassword(changePassword, resetRequest.token);

						// Check Reset Request
						if (isReset) {

							// Indicate Successful
							isSuccess = true;

							// Inform User
							notification.success({
								message: "Success",
								description: "Password successfully changed.  Please try logging in."
							});

							// Change Stage
							setStage(defaultStage);
						}
					}
					else {

						// Indicate Request Is Expired
						isExpired = true;
					}
				}
			}

			// Check For Success
			if (!isSuccess) {

				// Check For Expired Request
				if (isExpired) {

					// Inform User
					notification.error({
						message: "Unable To Change Password",
						description: "It appears that too much time may have passed since being informed of a required password change.  Please try logging in again."
					});

					// Set Error Message
					StateService.setCancelableState(() => setMessage("Error Changing Password"), cancellationToken);
				}
				else {

					// Inform User
					notification.error({
						message: "Error Changing Password",
						description: "An unexpected error has occurred.  Please contact your system administrator."
					});

					// Set Error Message
					StateService.setCancelableState(() => setMessage("Error Changing Password"), cancellationToken);
				}
			}
		}
	};


	if (isLoading) {

		return <Spin />;
	}
	else if (isLoggedIn && !hasSentLoginEvent) {

		EventDispatcher.dispatch(Event.LoggedIn);
		setHasSentLoginEvent(true);

		return <Spin />;
	}

	return (

		<div className={className}>

			<TransitionRoute on={stage} case={"login"}>
				<Form className="login-form">
					<Form.Item>
						<Input
							prefix={<Icon type="user" />}
							placeholder="Username"
							name="username"
							onChange={onLoginUsernameChange}
							value={loginUsername}
							onPressEnter={onLogin}
							autoFocus={true}
						/>
					</Form.Item>
					<Form.Item>
						<Input
							prefix={<Icon type="lock" />}
							type="password"
							placeholder="Password"
							name="password"
							onChange={onLoginPasswordChange}
							value={loginPassword}
							onPressEnter={onLogin}
						/>
					</Form.Item>
					<Form.Item className="action-wrapper">
						<div className="button-wrapper">
							<Button
								type="primary"
								onClick={onLogin}
							>
								Login
							</Button>
							<Button
								onClick={curry(onSetStage)(__, "reset")}
								type="link"
							>
								Forgot Password
							</Button>
						</div>
					</Form.Item>
				</Form>
			</TransitionRoute>

			<TransitionRoute on={stage} case={"reset"}>
				<Form className="reset-form">
					<Form.Item>
						<Input
							prefix={<Icon type="user" />}
							placeholder="Username"
							name="reset-username"
							onChange={onResetUsernameChange}
							value={resetUsername}
							onPressEnter={onReset}
							autoFocus={true}
						/>
					</Form.Item>
					<Form.Item className="action-wrapper">
						<div className="button-wrapper">
							<Button
								type="primary"
								onClick={onReset}
							>
								Reset
							</Button>
							<Button
								onClick={curry(onSetStage)(__, "login")}
								type="link"
							>
								Cancel
							</Button>
						</div>
					</Form.Item>
				</Form>
			</TransitionRoute>

			<TransitionRoute on={stage} case={"change"}>
				<Form className="change-form">
					<Form.Item>
						<Input
							prefix={<Icon type="lock" />}
							type="password"
							placeholder="Password"
							name="change-password"
							onChange={onChangePasswordChange}
							value={changePassword}
							onPressEnter={onChange}
							autoFocus={true}
						/>
					</Form.Item>
					<Form.Item>
						<Input
							prefix={<Icon type="lock" />}
							type="password"
							placeholder="Confirm Password"
							name="change-password-confirm"
							onChange={onChangePasswordConfirmChange}
							value={changePasswordConfirmation}
							onPressEnter={onChange}
						/>
					</Form.Item>
					<Form.Item className="action-wrapper">
						<div className="button-wrapper">
							<Button
								type="primary"
								onClick={onChange}
								disabled={!doChangePasswordsMatch}
							>
								Change
							</Button>
							<Button
								onClick={curry(onSetStage)(__, "login")}
								type="link"
							>
								Cancel
							</Button>
						</div>
					</Form.Item>
				</Form>
			</TransitionRoute>

			{(message !== undefined
				?
				<span className="error-message">{message}</span>
				:
				null
			)}
		</div >
	);
};

export class LoginView extends React.Component<ILoginViewProps> {

	public render() {

		return <LoginViewFC {...this.props} />;
	}
}
